import { useState } from 'react';

// Captured once at module load — stable reference for days-remaining calc.
const PAGE_LOAD_MS = Date.now();
import { useMutation } from '@tanstack/react-query';
import { useMe, useInvalidateMe } from '@/hooks/useMe';
import { withdrawalsApi } from '@/api/endpoints';
import { Card } from '@/components/ui/Card';
import { SavingsGoalCard } from './SavingsGoalCard';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { formatCents, parseDollarsToCents } from '@/utils/money';
import type { LockTranche } from '@/types/api';

// ─── Lock-tranche row ────────────────────────────────────────────────────────

function TrancheRow({ tranche }: { tranche: LockTranche }) {
  const until = tranche.lockUntil ? new Date(tranche.lockUntil) : null;
  const daysLeft = until
    ? Math.max(0, Math.ceil((until.getTime() - PAGE_LOAD_MS) / 86_400_000))
    : null;

  return (
    <div className="flex items-center justify-between py-2.5 border-b border-white/10 last:border-0">
      <div className="text-sm">
        {/* v0.11 — openCents is the principal still locked now (unlocks at
            term); lockedSavingsCents is the original amount for context. */}
        <div className="font-medium">{formatCents(tranche.openCents)}</div>
        {until ? (
          <div className="text-xs mt-0.5 inline-flex items-center gap-1.5">
            <span className={`w-1.5 h-1.5 rounded-full ${tranche.matured ? 'bg-brand-green' : 'bg-brand-teal'}`} />
            <span className="text-white/45">
              {tranche.matured
                ? 'Matured — awaiting daily unlock'
                : `Unlocks ${until.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}${daysLeft !== null ? ` (${daysLeft}d)` : ''}`}
            </span>
          </div>
        ) : (
          <div className="text-xs text-white/40">No lock date</div>
        )}
      </div>
      {tranche.lockTermMonths !== null && (
        <span className="text-xs text-white/30 bg-white/5 rounded px-2 py-0.5">
          {tranche.lockTermMonths}mo term
        </span>
      )}
    </div>
  );
}

// ─── Withdraw form ───────────────────────────────────────────────────────────

function WithdrawForm({ maxCents }: { maxCents: number }) {
  const invalidateMe = useInvalidateMe();

  const [dollars, setDollars]       = useState('');
  const [bsb, setBsb]               = useState('');
  const [acct, setAcct]             = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg]     = useState('');

  const amountCents = parseDollarsToCents(dollars || '0');
  const tooMuch     = amountCents > maxCents && maxCents > 0;
  const tooLittle   = amountCents > 0 && amountCents < 100;

  const withdraw = useMutation({
    mutationFn: () =>
      withdrawalsApi.create({
        amountCents,
        // In production this token comes from Monoova / Direct Entry PayID confirmation.
        // For staging / dev we encode the bank details as a plain string.
        destinationToken: `bsb:${bsb}|acct:${acct}`,
      }),
    onSuccess: () => {
      invalidateMe();
      setSuccessMsg(
        `Withdrawal of ${formatCents(amountCents)} initiated — expect funds within 1–2 business days after the 24 h cooling-off period.`,
      );
      setDollars('');
      setBsb('');
      setAcct('');
    },
    onError: (err: Error) => {
      setErrorMsg(err.message || 'Withdrawal failed — please try again.');
    },
  });

  const canSubmit =
    amountCents >= 100 &&
    amountCents <= maxCents &&
    bsb.replace(/\D/g, '').length === 6 &&
    acct.trim().length >= 4 &&
    !withdraw.isPending;

  return (
    <div className="flex flex-col gap-4">
      <Input
        label="Amount (AUD)"
        type="number"
        min="1"
        step="0.01"
        placeholder="e.g. 50"
        value={dollars}
        onChange={(e) => { setDollars(e.target.value); setSuccessMsg(''); setErrorMsg(''); }}
        error={
          tooMuch   ? `Maximum you can withdraw is ${formatCents(maxCents)}.` :
          tooLittle ? 'Minimum withdrawal is $1.00.' :
          undefined
        }
      />
      <div className="grid grid-cols-2 gap-3">
        <Input
          label="BSB"
          type="text"
          placeholder="000-000"
          value={bsb}
          onChange={(e) => setBsb(e.target.value)}
          maxLength={7}
        />
        <Input
          label="Account number"
          type="text"
          placeholder="12345678"
          value={acct}
          onChange={(e) => setAcct(e.target.value)}
          maxLength={10}
        />
      </div>
      <p className="text-xs text-white/30">
        A 24 h cooling-off period applies. You can cancel before it elapses from your Account page.
      </p>
      <Button
        onClick={() => withdraw.mutate()}
        disabled={!canSubmit}
        className="w-full"
      >
        {withdraw.isPending ? 'Requesting…' : amountCents >= 100 ? `Withdraw ${formatCents(amountCents)}` : 'Enter an amount'}
      </Button>
      {successMsg && <p className="text-sm text-brand-teal text-center">{successMsg}</p>}
      {errorMsg   && <p className="text-sm text-red-400 text-center">{errorMsg}</p>}
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function SavingsPage() {
  const { data: me } = useMe();
  const [showWithdraw, setShowWithdraw] = useState(false);

  const savings    = me?.balances.savingsCents ?? 0;
  const balance    = me?.balances.balanceCents ?? 0;
  const deposited  = me?.balances.cumulativeDepositsCents ?? 0;
  const withdrawn  = me?.balances.cumulativeWithdrawalsCents ?? 0;
  const inflight   = me?.balances.inflightWithdrawalsCents ?? 0;
  const feesPaid   = me?.balances.cumulativeMonthlyAdminFeesCents ?? 0;
  // Withdrawable cap = cumulative deposits − fees − completed withdrawals − in-flight withdrawals.
  // cumulativeWithdrawalsCents only counts completed ones (the ledger event fires on completion),
  // so we subtract inflight separately to prevent over-requesting during the cooling-off window.
  const withdrawable = Math.max(0, deposited - feesPaid - withdrawn - inflight);

  const tranches: LockTranche[] = me?.balances.lockTranches ?? [];
  const nextMaturityAt = me?.balances.nextMaturityAt;

  return (
    <div className="flex flex-col gap-6">
      {/* Balance hero */}
      <div className="grid grid-cols-2 gap-3">
        <div className="relative overflow-hidden rounded-2xl border border-brand-green/20 bg-gradient-to-br from-[#0e2e1c] to-[#082014] p-4">
          <div className="text-xs font-medium text-brand-green/80">Savings (locked)</div>
          <div className="mt-1 text-2xl font-bold tabular-nums text-white">{formatCents(savings)}</div>
        </div>
        <div className="relative overflow-hidden rounded-2xl border border-brand-teal/20 bg-gradient-to-br from-[#102f3d] to-[#0a1c2c] p-4">
          <div className="text-xs font-medium text-brand-teal/90">Play Balance</div>
          <div className="mt-1 text-2xl font-bold tabular-nums text-white">{formatCents(balance)}</div>
        </div>
      </div>

      <SavingsGoalCard userId={me?.user.id} savingsCents={savings} />

      {/* The numbers */}
      <Card title="The numbers">
        <div className="flex flex-col gap-0">
          <div className="flex justify-between py-2.5 border-b border-white/[0.07]">
            <span className="text-white/60">Total deposited</span>
            <span className="tabular-nums">{formatCents(deposited)}</span>
          </div>
          <div className="flex justify-between py-2.5 border-b border-white/[0.07]">
            <span className="text-white/60">Total withdrawn</span>
            <span className="text-white/60 tabular-nums">{formatCents(withdrawn)}</span>
          </div>
          <div className="flex justify-between py-2.5 border-b border-white/[0.07]">
            <span className="text-white/60">Fees paid</span>
            <span className="text-white/50 tabular-nums">{formatCents(feesPaid)}</span>
          </div>
          {inflight > 0 ? (
            <div className="flex justify-between py-2.5">
              <span className="text-amber-400/80 text-sm">Pending withdrawal (cooling off)</span>
              <span className="text-amber-400/80 text-sm tabular-nums">−{formatCents(inflight)}</span>
            </div>
          ) : (
            <div className="flex justify-between py-2.5">
              <span className="text-white/40 text-sm">No pending withdrawals</span>
              <span className="text-white/40 text-sm">—</span>
            </div>
          )}
        </div>
      </Card>

      {/* Withdrawal cap + CTA */}
      <Card title="Withdrawal cap">
        <p className="text-white/60 text-sm mb-3">
          You can withdraw up to your{' '}
          <strong className="text-white">withdrawable cap</strong> — real money you
          deposited minus fees paid and prior withdrawals. Savings unlock into your
          withdrawable Play Balance on the lock term or when you win a bet.
        </p>
        <div className="flex items-center justify-between py-3 bg-white/5 rounded-lg px-4 mb-4">
          <span className="text-white/70">Withdrawable cap</span>
          <span className="font-bold text-xl text-brand-teal">{formatCents(withdrawable)}</span>
        </div>
        {withdrawable > 0 && balance > 0 && (
          <Button
            variant="secondary"
            className="w-full mb-2"
            onClick={() => setShowWithdraw((v) => !v)}
          >
            {showWithdraw ? 'Cancel' : 'Withdraw funds'}
          </Button>
        )}
        {showWithdraw && (
          <div className="mt-4 pt-4 border-t border-white/10">
            <WithdrawForm maxCents={Math.min(balance, withdrawable)} />
          </div>
        )}
        {withdrawable === 0 && deposited === 0 && (
          <p className="text-sm text-white/40 text-center italic">
            Make a deposit to get started.
          </p>
        )}
      </Card>

      {/* Lock tranches */}
      {tranches.length > 0 && (
        <Card title="Savings tranches">
          {nextMaturityAt && (
            <p className="text-xs text-white/40 mb-3">
              Next maturity:{' '}
              <strong className="text-white/60">
                {new Date(nextMaturityAt).toLocaleDateString('en-AU', {
                  day: 'numeric', month: 'short', year: 'numeric',
                })}
              </strong>
              {' '}— the daily job will move it into your Play Balance automatically.
            </p>
          )}
          <div>
            {tranches.map((t, i) => (
              <TrancheRow key={i} tranche={t} />
            ))}
          </div>
          <p className="text-xs text-white/30 mt-3">
            Win a bet and the equivalent amount unlocks early.
            Need an early release on hardship grounds? Contact support.
          </p>
        </Card>
      )}
    </div>
  );
}
